import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface RotatingWordProps {
    words: string[];
    /** Time each word stays up, in ms */
    interval?: number;
    className?: string;
    /** Thin line under the word */
    underline?: boolean;
}

/**
 * A word that swaps in place inside a sentence. All words share one grid cell, so the slot is as wide as the
 * longest word and sits on the text baseline like any other word (it wraps with the text, never on its own).
 * Words just fade and slide a few pixels, so the type stays crisp (no blur or filter tricks).
 */
export function RotatingWord({ words, interval = 2600, className, underline = true }: RotatingWordProps) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (words.length < 2) return;
        const id = setInterval(() => setIndex((i) => (i + 1) % words.length), interval);
        return () => clearInterval(id);
    }, [words.length, interval]);

    const previous = (index - 1 + words.length) % words.length;

    return (
        <span className={cn("relative inline-grid whitespace-nowrap text-center align-baseline", className)}>
            {words.map((word, i) => (
                <span
                    key={word}
                    aria-hidden="true"
                    className={cn(
                        "col-start-1 row-start-1 transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none",
                        i === index
                            ? "translate-y-0 opacity-100"
                            : i === previous
                                ? "-translate-y-[0.3em] opacity-0"
                                : "translate-y-[0.3em] opacity-0"
                    )}
                >
                    {word}
                </span>
            ))}
            {underline && (
                <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-[2.5px] rounded-full bg-purple-500 dark:bg-purple-400" />
            )}
        </span>
    );
}

export default RotatingWord;
